/** Reklamationen tab — list service_tickets pro deal. */

import { Ic } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/empty";
import type { DealComplaint } from "@/lib/fetchers";
import { t } from "@/i18n/de";

const STATUS_LABEL: Record<string, { label: string; tone: string }> = {
  new: { label: "Offen", tone: "amber" },
  in_progress: { label: "In Bearbeitung", tone: "gold" },
  waiting: { label: "Wartet", tone: "amber" },
  resolved: { label: "Gelöst", tone: "green" },
  closed: { label: "Geschlossen", tone: "default" },
  cancelled: { label: "Storniert", tone: "default" },
};

const PRIORITY_LABEL: Record<string, string> = {
  low: "Niedrig",
  normal: "Mittel",
  high: "Hoch",
  urgent: "Kritisch",
};

interface ComplaintsTabProps {
  complaints: DealComplaint[];
  loading: boolean;
  onNew: () => void;
}

export function ComplaintsTab({ complaints, loading, onNew }: ComplaintsTabProps) {
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>{t.loading}</div>
    );
  }

  if (complaints.length === 0) {
    return (
      <div>
        <Empty title="Keine offenen Reklamationen.">
          Sobald Sie eine melden, sehen Sie sie hier.
        </Empty>
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Button variant="secondary" onClick={onNew}>
            <Ic.Alert size={14} /> {t.detail.actions.complaint}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <Button variant="secondary" size="sm" onClick={onNew}>
          <Ic.Plus size={12} /> {t.detail.actions.complaint}
        </Button>
      </div>
      <div className="card" style={{ overflow: "hidden" }}>
        {complaints.map((c, idx) => {
          const status = STATUS_LABEL[c.status] || { label: c.status, tone: "default" };
          return (
            <div
              key={c.id}
              style={{
                padding: 18,
                borderBottom:
                  idx === complaints.length - 1 ? "none" : "1px solid var(--border-subtle)",
                display: "flex",
                gap: 16,
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <span className="num" style={{ fontSize: 12, color: "var(--gold)" }}>
                    {c.ticket_number}
                  </span>
                  <span className={`badge badge-${status.tone === "default" ? "" : status.tone}`}>
                    {status.label}
                  </span>
                  <span
                    style={{
                      fontSize: 11.5,
                      color: "var(--text-3)",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {PRIORITY_LABEL[c.priority] || c.priority}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    marginBottom: 4,
                  }}
                >
                  {c.subject}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--text-2)",
                    lineHeight: 1.5,
                  }}
                >
                  {c.description}
                </div>
              </div>
              <div
                className="num"
                style={{ fontSize: 12, color: "var(--text-3)", width: 90, textAlign: "right" }}
              >
                {new Date(c.reported_at).toLocaleDateString("de-DE")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
